<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';

import ReorderableTable from '@/components/ReorderableTable.vue';
import FaqQuestionDialog from './dialogs/FaqQuestionDialog.vue';

import { useFaqStore } from '@/stores/faqs.store';
import type { FaqDto, Locales, ProjectModuleDto } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';

// CONST
const { t } = useI18n();
const $q = useQuasar();

// STORE
const faqStore = useFaqStore();
const { orderedFaqs } = storeToRefs(faqStore);

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
}>();

// COMPUTED
const columns = computed(() => [
  {
    name: 'question',
    label: t('global.label.question'),
    field: (row: FaqDto) => getTranslation(row, 'question', props.languageEnabled),
    align: 'left',
  },
]);

// LIFECYCLE
onMounted(async () => {
  if (props.module.id) {
    await faqStore.fetchFaqsByModuleId(props.module.id);
  }
});

// FUNCTIONS
const handleAddRow = () => {
  if (props.module.id) {
    const faq: FaqDto = faqStore.createFaq(props.module.id);
    $q.dialog({
      component: FaqQuestionDialog,
      componentProps: {
        mode: 'add',
        faq,
        languageEnabled: props.languageEnabled,
      },
    });
  }
};

const handleDeleteRow = (row: FaqDto) => {
  $q.dialog({
    title: t('global.dialogs.deleteTitle', { type: t('project-module.name') }),
    message: t('global.dialogs.deleteMessage', { type: t('project-module.name') }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await faqStore.deleteFaq(row);
  });
};

const handleRowClick = (row: FaqDto) => {
  $q.dialog({
    component: FaqQuestionDialog,
    componentProps: {
      mode: 'edit',
      faq: row,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleReorder = (source: FaqDto, target: FaqDto) => {
  faqStore.updateFaqs(source, target);
};
</script>
<template>
  <div class="question-list">
    <reorderable-table
      :rows="orderedFaqs"
      :columns="columns"
      :title="t('faq.title')"
      itemKey="id"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      @reorder="handleReorder"
    />
  </div>
</template>
